<div class="attachments-step">
  <div class="row">
    <div class="col-12">
      <!-- Step 1: Attachment Selection -->
      <div *ngIf="currentStep === 'attachments' && machine && !machineLoading">
        <!-- Loading compatible attachments from catalog -->
        <div class="mb-4 text-center py-5" *ngIf="suggestionsLoading">
          <h5 class="mb-2 d-flex align-items-center justify-content-center">
            <common-spinner-loader class="mr-2"></common-spinner-loader>Loading compatible attachments
          </h5>
          <p class="text-muted font-weight-light">Matching attachment types to your exact machine type id.</p>
        </div>

        <!-- Attachment selection -->
        <div *ngIf="!suggestionsLoading && attachmentCheckboxCards.length > 0" class="form-section mb-4">
          <h5 class="section-header mb-3">Which attachments do you have?</h5>
          <p class="fs-15 font-weight-light">
            Adding attachments improves your listing visibility. If you have multiple attachments of the same type (e.g. multiple buckets of different sizes),
            you will be able to specify them on the next step.
          </p>

          <div class="row">
            <div class="col-md-4 mb-3" *ngFor="let card of attachmentCheckboxCards">
              <common-checkbox-cards [checkboxCards]="[card]" (checkboxCardsChange)="onSingleAttachmentCheckboxChange(card.value, $event)">
              </common-checkbox-cards>
            </div>
          </div>

          <div class="text-center mt-4">
            <button type="button" class="btn btn-success btn-lg" (click)="proceedToConfiguration()" [disabled]="hasPendingOperations()">
              Continue to Configuration
              <i class="icon-arrow-right ml-2"></i>
            </button>
          </div>
        </div>

        <!-- No compatible attachments -->
        <div *ngIf="!suggestionsLoading && attachmentCheckboxCards.length === 0" class="text-center py-4">
          <p class="text-muted">No compatible attachments were found for this machine type. You can add attachments manually later.</p>
          <button type="button" class="btn btn-success btn-lg" (click)="proceedToConfiguration()">Continue to Configuration</button>
        </div>
      </div>

      <!-- Step 2: Attachment Configuration -->
      <div *ngIf="currentStep === 'configuration' && machine && !machineLoading">
        <div class="mb-4">
          <h5 class="section-header mb-0">Configure Rental Rates</h5>
        </div>

        <!-- Attachment Pricing Note -->
        <div class="alert alert-info d-flex align-items-start mb-4" role="alert">
          <i class="icon-info-circle fs-20 mr-2 mt-1"></i>
          <div>
            <strong>Attachment Pricing:</strong> The first standard attachment is included free with your machine rental. Rental prices will be added to the
            total for any additional (secondary) attachments.
          </div>
        </div>

        <div class="attachments-list mb-4">
          <!-- No attachments message -->
          <div class="text-center py-4" *ngIf="attachments.length === 0">
            <i class="icon-attachment fs-48 text-muted mb-3"></i>
            <p class="text-muted">No attachments selected from the previous step.</p>
          </div>

          <!-- Attachments -->
          <div class="row">
            <div class="col-md-4 mb-3" *ngFor="let attachment of attachments; let i = index">
              <div class="card h-100" [class.border-warning]="attachment.deferred">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">{{ attachment.name }}</h6>
                  <span *ngIf="attachment.deferred" class="badge badge-warning">Specs Required</span>
                </div>
                <div class="card-body d-flex flex-column">
                  <!-- Required Specifications -->
                  <div *ngIf="attachment.requiredSpecs?.length > 0" class="mb-3">
                    <label class="text-muted small font-weight-bold text-uppercase mb-2 d-block">Required Specifications</label>
                    <div class="form-group" *ngFor="let spec of attachment.requiredSpecs; let j = index">
                      <label [for]="'spec-' + i + '-' + j">
                        {{ spec.name }} <span *ngIf="spec.unitOfMeasure">({{ spec.unitOfMeasure }})</span> *
                      </label>
                      <select
                        *ngIf="spec.allowedValues?.length"
                        class="form-control"
                        [id]="'spec-' + i + '-' + j"
                        [(ngModel)]="spec.value"
                      >
                        <option [ngValue]="null" disabled>Select {{ spec.name }}</option>
                        <option *ngFor="let opt of spec.allowedValues" [ngValue]="opt">{{ opt }}</option>
                      </select>
                      <input
                        *ngIf="!spec.allowedValues?.length"
                        type="number"
                        class="form-control"
                        [id]="'spec-' + i + '-' + j"
                        [(ngModel)]="spec.value"
                        [placeholder]="'Enter ' + spec.name.toLowerCase()"
                        min="0"
                        step="0.1"
                      />
                    </div>
                    <hr />
                  </div>

                  <!-- Weekly Rate -->
                  <div class="form-group">
                    <label [for]="'weeklyRate' + i">Weekly Rate *</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text">$</span>
                      </div>
                      <input type="number" class="form-control" [id]="'weeklyRate' + i" [(ngModel)]="attachment.weeklyRate" placeholder="0" min="0" step="1" />
                    </div>
                  </div>

                  <!-- Monthly Rate -->
                  <div class="form-group">
                    <label [for]="'monthlyRate' + i">Monthly Rate *</label>
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text">$</span>
                      </div>
                      <input
                        type="number"
                        class="form-control"
                        [id]="'monthlyRate' + i"
                        [(ngModel)]="attachment.monthlyRate"
                        placeholder="0"
                        min="0"
                        step="1"
                      />
                    </div>
                  </div>

                  <div class="mt-auto pt-3">
                    <div *ngIf="canAddAnotherAttachment(attachment.catalogItemId)">
                      <button
                        type="button"
                        class="btn btn-outline-success btn-sm w-100"
                        (click)="addAnotherAttachment(i)"
                        [disabled]="loading || pendingOperations.has(attachment.catalogItemId)"
                      >
                        Add another
                      </button>
                    </div>
                    <button type="button" class="btn btn-outline-danger btn-sm w-100 mt-2" (click)="removeAttachment(i)" [disabled]="loading || pendingOperations.has(attachment.catalogItemId)">
                      Remove Attachment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="text-center">
          <p *ngIf="hasIncompleteRequiredSpecs() && !loading" class="text-warning small mb-2">
            Please fill in all required specifications before continuing.
          </p>
          <button type="button" class="btn btn-success btn-lg" (click)="onNext()" [disabled]="loading || hasPendingOperations() || hasIncompleteRequiredSpecs()">
            <span *ngIf="loading" class="spinner-border spinner-border-sm mr-2"></span>
            {{ loading ? "Saving..." : "Continue to Photos" }}
          </button>

          <div class="mt-3">
            <button type="button" class="btn btn-link text-success" (click)="goBackToSelection()" [disabled]="loading">
              <i class="icon-arrow-left mr-1"></i>
              Back to attachments
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
